import React, { Component } from 'react';
import {
  array,
  arrayOf,
  bool,
  func,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { formatDayParam, parseDayParamToDate } from '../../../util/availability';
import {
  createResourceLocatorString,
  matchPathname,
  pathByRouteName,
} from '../../../util/routes';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  AvatarSmall,
  Button,
  // Imported from the components barrel ON PURPOSE: the barrel exports this as a
  // loadable() component, so react-dates ships in its own chunk. Importing the
  // file directly (as this did) bypasses the code-split and pulls react-dates
  // (~272 KB of pre-transpiled ES5) into the initial load of EVERY page, even
  // though the date picker only renders once the user opens the date filter.
  FieldDateRangeController,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

import css from './Topbar.module.css';
import IconCard from '../../../components/SavedCardDetails/IconCard/IconCard';
import FilterForm from '../../SearchPage/FilterForm/FilterForm';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({
    [modalStateParam]: 'open',
    ...parse(search),
  })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      const testURL = new URL('http://my.marketplace.com' + href);
      const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
      if (matchedRoutes.length > 0) {
        const found = matchedRoutes[0];
        const to = { search: testURL.search, hash: testURL.hash };
        return {
          ...linkConfig,
          route: {
            name: found.route?.name,
            params: found.params,
            to,
          },
        };
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found => {
  const name = found.route?.name;
  // Public CMS pages have their own routes named 'CMSPage_<pageId>' (no route params).
  if (name?.startsWith('CMSPage_')) {
    return `CMSPage:${name.slice('CMSPage_'.length)}`;
  }
  return name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
};
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName
      ? cmsPageName
      : inboxPageName
        ? inboxPageName
        : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      checkSubscription: false,
      hideInviteBanner: false,
      isSearchModalOpen: false,
      selectedDates: null,
      selectedBedrooms: '1',
      selectedGuests: '1',
      dateRange: {
        startDate: null,
        endDate: null,
      },
      showdate: '',
      isMobileDateOpen: false,
      bedrooms: 1,
      guestCount: 1,
      activeMobileFilter: null,
      // Must default to the value the server renders with, otherwise the first
      // client render disagrees with the SSR markup and React re-creates this
      // subtree during hydration (which invalidates the LCP candidate).
      // The real viewport value is read in componentDidMount.
      isMobileLayout: true,
    };
    this.mobileLayoutMql = null;
    this.handleMobileLayoutChange = this.handleMobileLayoutChange.bind(this);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleInviteBannerClose = this.handleInviteBannerClose.bind(this);
    this.handleSearchModalOpen = this.handleSearchModalOpen.bind(this);
    this.handleSearchModalClose = this.handleSearchModalClose.bind(this);
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  handleMobileLayoutChange(e) {
    this.setState({ isMobileLayout: e.matches });
  }

  componentDidMount() {
    const { onGetFacets, location } = this.props;
    if (onGetFacets) {
      onGetFacets();
    }

    // Resolve the real viewport size only after hydration, then keep it in sync.
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
      this.mobileLayoutMql = mql;
      if (mql.matches !== this.state.isMobileLayout) {
        this.setState({ isMobileLayout: mql.matches });
      }
      // Safari < 14 only supports the deprecated addListener API.
      if (mql.addEventListener) {
        mql.addEventListener('change', this.handleMobileLayoutChange);
      } else if (mql.addListener) {
        mql.addListener(this.handleMobileLayoutChange);
      }
    }

    // Read URL parameters and set state
    const urlParams = new URLSearchParams(location.search);
    const bedroomsParam = urlParams.get('Bedrooms');
    const guestsParam = urlParams.get('maximumGuests');

    if (bedroomsParam) {
      // Extract the first number from "1 TO 20" format
      const bedroomsMatch = bedroomsParam.match(/^(\d+)/);
      if (bedroomsMatch) {
        const bedroomsValue = parseInt(bedroomsMatch[1], 10);
        this.setState({
          bedrooms: bedroomsValue,
          selectedBedrooms: String(bedroomsValue),
        });
      }
    }

    if (guestsParam) {
      // Extract the first number from "1 TO 20" format
      const guestsMatch = guestsParam.match(/^(\d+)/);
      if (guestsMatch) {
        const guestsValue = parseInt(guestsMatch[1], 10);
        this.setState({
          guestCount: guestsValue,
          selectedGuests: String(guestsValue),
        });
      }
    }

    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');
    const startDate = parseDayParamToDate(startDateParam);
    const endDate = parseDayParamToDate(endDateParam);
    if (startDate && endDate) {
      this.setState({
        dateRange: { startDate, endDate },
        showdate: { dates: { startDate, endDate } },
        selectedDates: `${startDate.toISOString()}_${endDate.toISOString()}`,
      });
    }
  }

  componentWillUnmount() {
    const mql = this.mobileLayoutMql;
    if (mql) {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', this.handleMobileLayoutChange);
      } else if (mql.removeListener) {
        mql.removeListener(this.handleMobileLayoutChange);
      }
      this.mobileLayoutMql = null;
    }
  }

  // componentDidMount() {
  //   const { currentUser ,isAuthenticated} = this.props;
  //   console.log('currentUser :>> ', currentUser);
  //   const { Marketplace_Access } = currentUser && currentUser?.id && currentUser?.attributes?.profile?.publicData || {};
  //   console.log('Marketplace_Access :>> ', Marketplace_Access);
  //   if ( !Marketplace_Access && isAuthenticated) {
  //     this.setState({ checkSubscription: true });
  //   }
  // }
  // componentDidUpdate(prevProps) {
  //   const { currentUser, isAuthenticated } = this.props;
  //   console.log('currentUser :>> ', currentUser);
  //   console.log('currentUser?.attributes?.profile?.publicData?.Marketplace_Access :>> ', currentUser?.attributes?.profile?.publicData?.Marketplace_Access);
  //   if (
  //     isAuthenticated &&

  //     !currentUser?.attributes?.profile?.publicData?.Marketplace_Access

  //   ) {
  //     this.setState({ checkSubscription: true });
  //   }
  // }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSearchModalOpen() {
    this.setState({ isSearchModalOpen: true });
  }

  handleSearchModalClose() {
    this.setState({ isSearchModalOpen: false });
  }

  handleSearchSubmit(e) {
    e.preventDefault();
    const { history } = this.props;
    const { dateRange, bedrooms, guestCount } = this.state;

    // Close modal before navigating
    this.handleSearchModalClose();

    // Build search params like desktop TopbarDesktop (Bedrooms / maximumGuests / dates)
    const searchParams = new URLSearchParams();

    if (bedrooms > 0) {
      const maxBedrooms = 20;
      const algoliaBedroomsRange = `${bedrooms} TO ${maxBedrooms}`;
      searchParams.set('Bedrooms', algoliaBedroomsRange);
    }

    if (guestCount > 0) {
      const maxGuests = 20;
      const algoliaGuestRange = `${guestCount} TO ${maxGuests}`;
      searchParams.set('maximumGuests', algoliaGuestRange);
    }

    if (dateRange?.startDate && dateRange?.endDate) {
      // Calendar days, matching what AlgoliaSearchPage.duck.js expects.
      searchParams.set('startDate', formatDayParam(dateRange.startDate));
      searchParams.set('endDate', formatDayParam(dateRange.endDate));
    }

    history.push(`/s?${searchParams.toString()}`);
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  }
  handleCheckProfileModalClose() {
    this.setState({ checkSubscription: false });
  }
  handleInviteBannerClose() {
    const { isAuthenticated, currentUser, onUpdateProfile } = this.props;
    const currentPrivateData =
      currentUser?.attributes?.profile?.privateData || {};

    this.setState({ hideInviteBanner: true });

    if (isAuthenticated && currentUser?.id && onUpdateProfile) {
      const updatedPrivateData = {
        ...currentPrivateData,
        inviteBannerDismissedAt: new Date().toISOString(),
      };
      onUpdateProfile({ privateData: updatedPrivateData });
    }
  }
  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        searchParams
      )
    );
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    // Reset the subscription check state when logging out
    this.setState({ checkSubscription: false });
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      isLoggedInAs,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      intl,
      history,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      facets,
      routeConfiguration,
      onUpdateProfile, // eslint-disable-line no-unused-vars
    } = this.props;
    const {
      showdate,
      isMobileDateOpen,
      dateRange,
      bedrooms,
      guestCount,
      activeMobileFilter,
    } = this.state;
    const userId = currentUser?.id?.uuid;
    const safeLocation = location || { search: '' };
    const {
      mobilemenu,
      mobilesearch,
      keywords,
      address,
      origin,
      bounds,
    } = parse(safeLocation.search || '', {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(
      sortedCustomLinks,
      routeConfiguration
    );
    const resolvedCurrentPage =
      currentPage || getResolvedCurrentPage(location, routeConfiguration);

    // const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const countUnreadMessages = transactions => {
      let Count = 0;
      transactions &&
        transactions.length &&
        transactions?.forEach(tx => {
          const { metadata } = tx?.attributes || {};
          const checkInquiryTabs =
            currentUser?.id?.uuid ===
            tx?.relationships?.customer?.data?.id?.uuid;
          const { metaData } = metadata || {};
          const {
            isprovider,
            iscustomer,
            isproviderInquiry,
            isCustomer,
            isProvider,
          } = metaData || {};

          if (!checkInquiryTabs && isprovider === 'unread') {
            Count++;
          }
          if (checkInquiryTabs && iscustomer === 'unread') {
            Count++;
          }
          if (
            (!checkInquiryTabs && isproviderInquiry === 'unread') ||
            (!checkInquiryTabs && isProvider === 'unread')
          ) {
            Count++;
          }
          if (checkInquiryTabs && isCustomer === 'unread') {
            Count++;
          }
        });

      return {
        Count: Count >= 9 ? '9+' : Count,
      };
    };

    const unreadCounts = countUnreadMessages(notificationCount);
    const { Count } = unreadCounts || 0;
    const inviteBannerDismissedAt =
      currentUser?.attributes?.profile?.privateData?.inviteBannerDismissedAt;
    const shouldShowInviteBanner =
      !this.state.hideInviteBanner && !inviteBannerDismissedAt;

    const { isMobileLayout } = this.state;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        Count={Count}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={resolvedCurrentPage}
        customLinks={customLinks}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();
    const inviteLinkName =
      isAuthenticated && userId ? 'InviteFamiliesPage' : 'LoginPage';
    const inviteLinkParams = isAuthenticated && userId ? { id: userId } : {};

    const classes = classNames(rootClassName || css.root, className);

    const bedroomOptions = [0, 1, 2, 3, 4, 5, 6];
    const guestOptions = Array.isArray(facets?.maximumGuests)
      ? facets.maximumGuests
      : [];

    const toggleMobileFilter = filterName => {
      const normalized = (filterName || '').toLowerCase();
      this.setState(prevState => ({
        activeMobileFilter:
          prevState.activeMobileFilter === normalized ? null : normalized,
      }));
    };

    const mobileDateLabel =
      dateRange?.startDate && dateRange?.endDate
        ? `${intl.formatDate(dateRange.startDate, {
          month: 'short',
          day: 'numeric',
        })} - ${intl.formatDate(dateRange.endDate, {
          month: 'short',
          day: 'numeric',
        })}`
        : intl.formatMessage({ id: 'TopbarDesktop.filter.dates' });

    const closeMobileFilters = () => {
      this.setState({ activeMobileFilter: null });
    };

    // Mobile date filter behaviour – mirror desktop date picker logic in a simpler way
    const handleRefineDateRange = values => {
      if (!values || !values.dates) {
        return;
      }

      const { dates } = values;
      this.setState(prevState => ({
        dateRange: {
          startDate: dates.startDate || null,
          endDate: dates.endDate || null,
        },
        showdate: values,
        // Store a simple string representation so the existing submit handler can use it
        selectedDates:
          dates.startDate && dates.endDate
            ? `${dates.startDate.toISOString()}_${dates.endDate.toISOString()}`
            : prevState.selectedDates,
      }));
    };

    const handleClearFilters = () => {
      this.setState({
        dateRange: {
          startDate: null,
          endDate: null,
        },
        showdate: '',
        selectedDates: null,
      });
    };

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={resolvedCurrentPage}
        />

        {shouldShowInviteBanner && isAuthenticated ? (
          <div className={css.InfoTopBarBanner}>
            {isAuthenticated && (
              <>
                <NamedLink
                  className={css.infoBannerLink}
                  name={inviteLinkName}
                  params={inviteLinkParams}
                >
                  <FormattedMessage id="Topbar.infoBannerText" />
                </NamedLink>

                <Button
                  className={css.infoBannerCloseBtn}
                  onClick={this.handleInviteBannerClose}
                >
                  <IconCard brand="modalClose" />
                </Button>
              </>
            )}
          </div>
        ) : null}

        <div
          className={classNames(
            mobileRootClassName || css.container,
            mobileClassName
          )}
        >
          {/* Note: LinkedLogo renders its own <a>. It must NOT be wrapped in
              another <a> -- nested anchors are invalid HTML, so the parser
              auto-closes the outer one and the hydrated DOM stops matching the
              server markup. That mismatch makes React tear down and re-create
              this subtree, which invalidates the LCP candidate. */}
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage(
              { id: 'TopbarDesktop.logo' },
              // { marketplaceName }
            )}
            linkToExternalSite={config?.topbar?.logoLink}
          />

          <div className={css.btnWrapper}>
            <div className={css.btnContainer}>
              <button
                type="button"
                aria-label={intl.formatMessage({ id: 'Topbar.searchIcon' })}
                className={classNames(
                  css.profileLiinks,
                  css.searchIcon,
                  css.searchButton
                )}
                onClick={this.handleSearchModalOpen}
              >
                <IconCard brand="mobileSearch" />
              </button>
            </div>

            <Button
              rootClassName={css.menu}
              onClick={this.handleMobileMenuOpen}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              <MenuIcon className={css.menuIcon} />
              <AvatarSmall
                className={css.avatar}
                user={currentUser}
                disableProfileLink
              />
            </Button>

            {/* Search Modal */}
            <Modal
              id="search-modal"
              isOpen={this.state.isSearchModalOpen}
              onClose={this.handleSearchModalClose}
              containerClassName={css.searchModalContainer}
              closeButtonMessage="Close search"
              onManageDisableScrolling={onManageDisableScrolling}
              className={css.searchModal}
            >
              <div className={css.searchModalContent}>
                <div className={css.searchModalHeader}>
                  <h2 className={css.searchModalTitle}>Discover Homes</h2>
                </div>

                <form
                  onSubmit={this.handleSearchSubmit}
                  className={css.searchForm}
                >
                  <div className={css.searchWrapper}>
                    <div className={css.searchField}>
                      <button
                        type="button"
                        className={css.dateFilterToggleButton}
                        onClick={() =>
                          this.setState({ isMobileDateOpen: !isMobileDateOpen })
                        }
                      >
                        <IconCard brand="topbarCalendar" />
                        <span className={css.guestCount}>{mobileDateLabel}</span>
                        <IconCard
                          brand={isMobileDateOpen ? 'chevronUp' : 'chevronDown'}
                        />
                      </button>

                      {isMobileDateOpen ? (
                        <div className={css.mobileDatePopover}>
                          <div className={css.mobileDatePopoverInner}>
                            <div className={css.mobileFilterHeaderRow}>
                              <span className={css.mobileFilterCardHeader}>
                                Dates
                              </span>
                              <button
                                type="button"
                                className={css.mobileFilterCloseButton}
                                onClick={() =>
                                  this.setState({ isMobileDateOpen: false })
                                }
                              >
                                <IconCard brand="modalClose" />
                              </button>
                            </div>
                            <FilterForm
                              id="topbarMobileDateFilter"
                              handleClearFilters={handleClearFilters}
                              setShowdate={values =>
                                this.setState({ showdate: values })
                              }
                              initialValues={{
                                dates: {
                                  startDate:
                                    showdate?.dates?.startDate ||
                                    showdate?.dates?.start ||
                                    null,
                                  endDate:
                                    showdate?.dates?.endDate ||
                                    showdate?.dates?.end ||
                                    null,
                                },
                              }}
                              onSubmit={values => {
                                handleRefineDateRange(values);
                                this.setState({ isMobileDateOpen: false });
                              }}
                              onCancel={() => {
                                handleClearFilters();
                                this.setState({ isMobileDateOpen: false });
                              }}
                              onClear={() => {
                                handleClearFilters();
                                this.setState({ isMobileDateOpen: false });
                              }}
                            >
                              <FieldDateRangeController name="dates" />
                              {/* Informational only: the 30-day minimum is enforced
                                  when a booking request is submitted, not here. */}
                              <p className={css.minStayNotice}>
                                {intl.formatMessage({
                                  id: 'TopbarDesktop.filter.minStayNotice',
                                })}
                              </p>
                            </FilterForm>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className={css.spacer}></div>

                    <div className={css.searchField}>
                      <button
                        type="button"
                        className={css.dateFilterToggleButton}
                        onClick={() => toggleMobileFilter('bedrooms')}
                      >
                        <IconCard brand="topbarBedrooms" />
                        <span className={css.guestCount}>
                          {intl.formatMessage({
                            id: 'TopbarDesktop.filter.bedrooms',
                          })} {bedrooms > 1 ? `- ${bedrooms}+` : ''}
                        </span>
                        <IconCard
                          brand={
                            activeMobileFilter === 'bedrooms'
                              ? 'chevronUp'
                              : 'chevronDown'
                          }
                        />
                      </button>

                      {activeMobileFilter === 'bedrooms' ? (
                        <div className={css.mobileDatePopover}>
                          <div className={css.mobileDatePopoverInner}>
                            <div className={css.mobileFilterHeaderRow}>
                              <span className={css.mobileFilterCardHeader}>
                                Bedrooms
                              </span>
                              {/* <button
                                type="button"
                                className={css.mobileFilterCloseButton}
                                onClick={closeMobileFilters}
                              >
                                <IconCard brand="modalClose" />
                              </button> */}
                            </div>
                            {/* <div className={css.mobileFilterSelectRow}>
                              <select
                                id="topbarMobileBedrooms"
                                className={css.searchInput}
                                value={bedrooms}
                                onChange={e => {
                                  const value = Number(e.target.value);
                                  this.setState({
                                    bedrooms: value,
                                    selectedBedrooms:
                                      value === 0 ? '' : String(value),
                                  });
                                }}
                              >
                                <option value={0}>
                                  {intl.formatMessage({
                                    id: 'TopbarDesktop.filter.bedrooms',
                                  })}
                                </option>
                                {bedroomOptions
                                  .filter(option => option !== 0)
                                  .map(option => (
                                    <option key={option} value={option}>
                                      {option} {option === 1 ? '+' : '+'}
                                    </option>
                                  ))}
                              </select>
                            </div> */}
                            <div className={css.mobileFilterSelectRow}>
                              <button
                                type="button"
                                className={css.decrementButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newBedrooms = Math.max(1, bedrooms - 1);
                                  this.setState({
                                    bedrooms: newBedrooms,
                                    selectedBedrooms: String(newBedrooms),
                                  });
                                }}
                                disabled={bedrooms <= 1}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="2" viewBox="0 0 13 2" fill="none">
                                  <path d="M0 0.875C0 0.642936 0.0921874 0.420376 0.256282 0.256282C0.420376 0.0921874 0.642936 0 0.875 0H11.375C11.6071 0 11.8296 0.0921874 11.9937 0.256282C12.1578 0.420376 12.25 0.642936 12.25 0.875C12.25 1.10706 12.1578 1.32962 11.9937 1.49372C11.8296 1.65781 11.6071 1.75 11.375 1.75H0.875C0.642936 1.75 0.420376 1.65781 0.256282 1.49372C0.0921874 1.32962 0 1.10706 0 0.875Z" fill="#712739" />
                                </svg>
                              </button>
                              <span className={css.guestCountDropdown}>
                                {bedrooms}+
                              </span>
                              <button
                                type="button"
                                className={css.incrementButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newBedrooms = Math.min(10, bedrooms + 1);
                                  this.setState({
                                    bedrooms: newBedrooms,
                                    selectedBedrooms: String(newBedrooms),
                                  });
                                }}
                                disabled={bedrooms >= 10}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                                  <path d="M4.375 10.5H10.5M10.5 10.5H16.625M10.5 10.5V4.375M10.5 10.5V16.625" stroke="#712739" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </button>
                            </div>
                            {/* <div className={css.mobileFilterActionsRow}>
                              <button
                                type="button"
                                className={css.mobileClearButton}
                                onClick={() => {
                                  this.setState({
                                    bedrooms: 0,
                                    selectedBedrooms: '',
                                  });
                                }}
                              >
                                Clear
                              </button>
                              <button
                                type="button"
                                className={css.mobileApplyButton}
                                onClick={closeMobileFilters}
                              >
                                Apply
                              </button>
                            </div> */}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className={css.spacer}></div>

                    <div className={css.searchField}>
                      <button
                        type="button"
                        className={css.dateFilterToggleButton}
                        onClick={() => toggleMobileFilter('guests')}
                      >
                        <IconCard brand="topbarGuestUsers" />
                        <span className={css.guestCount}>
                          {intl.formatMessage({
                            id: 'TopbarDesktop.filter.guests',
                          })} {guestCount > 1 ? `- ${guestCount}` : ''}
                        </span>
                        <IconCard
                          brand={
                            activeMobileFilter === 'guests'
                              ? 'chevronUp'
                              : 'chevronDown'
                          }
                        />
                      </button>

                      {activeMobileFilter === 'guests' ? (
                        <div className={css.mobileDatePopover}>
                          <div className={css.mobileDatePopoverInner}>
                            <div className={css.mobileFilterHeaderRow}>
                              <span className={css.mobileFilterCardHeader}>
                                Guests
                              </span>
                              {/* <button
                                type="button"
                                className={css.mobileFilterCloseButton}
                                onClick={closeMobileFilters}
                              >
                                <IconCard brand="modalClose" />
                              </button> */}
                            </div>

                            <div className={css.mobileFilterSelectRow}>
                              <button
                                type="button"
                                className={css.decrementButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newGuestCount = Math.max(1, guestCount - 1);
                                  this.setState({
                                    guestCount: newGuestCount,
                                    selectedGuests: String(newGuestCount),
                                  });
                                }}
                                disabled={guestCount <= 1}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="2" viewBox="0 0 13 2" fill="none">
                                  <path d="M0 0.875C0 0.642936 0.0921874 0.420376 0.256282 0.256282C0.420376 0.0921874 0.642936 0 0.875 0H11.375C11.6071 0 11.8296 0.0921874 11.9937 0.256282C12.1578 0.420376 12.25 0.642936 12.25 0.875C12.25 1.10706 12.1578 1.32962 11.9937 1.49372C11.8296 1.65781 11.6071 1.75 11.375 1.75H0.875C0.642936 1.75 0.420376 1.65781 0.256282 1.49372C0.0921874 1.32962 0 1.10706 0 0.875Z" fill="#712739" />
                                </svg>
                              </button>
                              <span className={css.guestCountDropdown}>
                                {guestCount}
                              </span>
                              <button
                                type="button"
                                className={css.incrementButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newGuestCount = Math.min(12, guestCount + 1);
                                  this.setState({
                                    guestCount: newGuestCount,
                                    selectedGuests: String(newGuestCount),
                                  });
                                }}
                                disabled={guestCount >= 12}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                                  <path d="M4.375 10.5H10.5M10.5 10.5H16.625M10.5 10.5V4.375M10.5 10.5V16.625" stroke="#712739" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </button>
                            </div>

                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <button type="submit" className={css.searchButtonWrapper}>
                    <IconCard brand="mobileSearch" />
                    Search Homes
                  </button>
                </form>
              </div>
            </Modal>

            {/* {notificationDot} */}
          </div>
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            Count={Count}
            onManageDisableScrolling={onManageDisableScrolling}
            currentPage={resolvedCurrentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={Count}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            config={config}
            customLinks={customLinks}
            facets={facets}
            location={location}
          />
        </div>
        <Modal
          id="TopbarMobileMenu"
          className={!isAuthenticated ? css.loggedOutModal : ""}
          containerClassName={css.modalContainer}
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainerSearchForm}
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <TopbarSearchForm
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
              isMobile
              appConfig={config}
            />
            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />
        <Modal
          id="ListingPublishedModal"
          className={css.modalContainer}
          isOpen={this.state.checkSubscription}
          closed={true}
          onClose={this.handleCheckProfileModalClose}
          onManageDisableScrolling={onManageDisableScrolling}
          usePortal
        >
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div className={css.borderBottom}></div>
            <p className={css.modalDescription}>
              <FormattedMessage id="EditListingPage.CheckSubscriptionTitle" />
            </p>
            <button onClick={this.handleLogout}>Logout</button>
          </div>
        </Modal>
        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
  facets: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,
  onUpdateProfile: func,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  facets: array,
  onGetFacets: func,
};

const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
